<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">{{ editing ? "Editar cupón" : "Nuevo cupón" }}</h1>
    </div>
    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card tag="article" no-body style="max-width: 820px" class="custom-card">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>{{ editing ? "Datos del cupón" : "Datos del nuevo cupón" }}</h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <!-- ══ Sección 1 — Datos básicos ══ -->
            <section class="form-section">
              <h2 class="form-subtitle">Datos básicos</h2>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="code">Código *</label>
                    <input id="code" type="text" v-model.trim="form.code" autocomplete="off"
                      placeholder="Ej: VERANO25" class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.code.$error }"
                      :disabled="editing" style="text-transform: uppercase" />
                    <FormError v-if="$v.form.code.$error && !$v.form.code.required"
                      message="El código es obligatorio" />
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group">
                    <label for="description">Nombre / descripción</label>
                    <input id="description" type="text" v-model.trim="form.description" autocomplete="off"
                      placeholder="Descripción del cupón" class="custom-form-control" />
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group">
                    <label for="coupon_type">Tipo de cupón *</label>
                    <FormSelect id="coupon_type" v-model="form.coupon_type" :items="couponTypes" />
                  </div>
                </div>

                <!-- Valor: visible si NO es lista de precios -->
                <div class="col-md-6" v-if="!isPriceList">
                  <div class="form-group">
                    <label for="discount_value">Valor *</label>
                    <input id="discount_value" type="number" step="0.01" min="0.01"
                      v-model="form.discount_value" autocomplete="off"
                      :placeholder="valuePlaceholder" class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.discount_value.$error }" />
                    <FormError v-if="$v.form.discount_value.$error && !$v.form.discount_value.required"
                      message="El valor es obligatorio" />
                  </div>
                </div>

                <!-- Lista de precios: visible SOLO si es lista de precios -->
                <div class="col-md-6" v-if="isPriceList">
                  <div class="form-group">
                    <label for="price_type_id">Lista de precios *</label>
                    <FormSelect id="price_type_id" v-model="form.price_type_id" :items="priceTypeItems"
                      defaultOption="Elegir lista de precios"
                      :showError="$v.form.price_type_id.$error && !$v.form.price_type_id.required" />
                    <FormError v-if="$v.form.price_type_id.$error && !$v.form.price_type_id.required"
                      message="La lista de precios es obligatoria" />
                  </div>
                </div>
              </div>
            </section>

            <!-- ══ Sección 2 — Vigencia y límites ══ -->
            <section class="form-section">
              <h2 class="form-subtitle">Vigencia y límites</h2>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="start_date">Fecha de inicio</label>
                    <input id="start_date" type="date" v-model="form.start_date" class="custom-form-control" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="end_date">Fecha de fin</label>
                    <input id="end_date" type="date" v-model="form.end_date" class="custom-form-control" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="max_uses">Usos máximos totales</label>
                    <input id="max_uses" type="number" min="1" v-model="form.max_uses"
                      placeholder="Sin límite" class="custom-form-control" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-0">
                    <label for="max_uses_per_user">Usos máximos por persona</label>
                    <input id="max_uses_per_user" type="number" min="1" v-model="form.max_uses_per_user"
                      placeholder="Sin límite" class="custom-form-control" />
                  </div>
                </div>
              </div>
            </section>

            <!-- ══ Sección 3 — Restricciones avanzadas (colapsable) ══ -->
            <section class="form-section">
              <button type="button" class="adv-toggle" @click="showAdvanced = !showAdvanced">
                <i :class="showAdvanced ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
                Restricciones avanzadas
              </button>

              <div v-show="showAdvanced" class="mt-3">
                <div class="row">
                  <!-- Ciudad -->
                  <div class="col-md-6">
                    <div class="form-group">
                      <label for="city_id">Ciudad</label>
                      <FormSelect id="city_id" v-model="form.city_id" :items="cityItems" />
                    </div>
                  </div>

                  <!-- Distritos (solo si hay ciudad específica) -->
                  <div class="col-md-6" v-if="form.city_id">
                    <div class="form-group">
                      <label for="district_ids">Distritos permitidos</label>
                      <select id="district_ids" multiple v-model="form.district_ids"
                        class="custom-form-control district-multi">
                        <option v-for="d in districts" :key="d.id" :value="d.id">{{ d.name }}</option>
                      </select>
                      <small class="text-muted">Vacío = todos los distritos. Ctrl/Cmd para multiselección.</small>
                    </div>
                  </div>
                </div>

                <!-- Tipos de vehículo -->
                <div class="form-group">
                  <label>Tipos de vehículo permitidos</label>
                  <div class="d-flex flex-wrap">
                    <label v-for="vt in vehicleOptions" :key="vt" class="check-inline">
                      <input type="checkbox" :value="vt" v-model="form.vehicle_types" /> {{ vt }}
                    </label>
                  </div>
                  <small class="text-muted">Vacío = todos los vehículos.</small>
                </div>

                <!-- Ruta -->
                <div class="form-group">
                  <label>Ruta</label>
                  <div class="d-flex flex-wrap">
                    <label class="check-inline">
                      <input type="radio" value="" v-model="form.route_type" /> Ambas
                    </label>
                    <label class="check-inline">
                      <input type="radio" value="HOT_APTO" v-model="form.route_type" /> Solo Hotel → Aeropuerto
                    </label>
                    <label class="check-inline">
                      <input type="radio" value="APTO_HOT" v-model="form.route_type" /> Solo Aeropuerto → Hotel
                    </label>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group mb-0">
                      <label for="max_passengers">Máx. pasajeros</label>
                      <input id="max_passengers" type="number" min="1" v-model="form.max_passengers"
                        placeholder="Sin límite" class="custom-form-control" />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group mb-0">
                      <label for="max_luggage">Máx. maletas</label>
                      <input id="max_luggage" type="number" min="0" v-model="form.max_luggage"
                        placeholder="Sin límite" class="custom-form-control" />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section class="form-section">
              <div class="form-group-buttons text-right">
                <router-link :to="{ name: 'config-coupon-list' }" class="button button-light">
                  {{ editing ? "Volver" : "Cancelar" }}
                </router-link>
                <button class="button button-primary" type="submit">Guardar</button>
              </div>
            </section>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormSelect, FormError } from "wize-admin";
import { required } from "vuelidate/lib/validators";
import { CouponService, GeographyService, PriceTypeService } from "@/core/services";

const COUPON_TYPES = [
  { key: "PERCENTAGE", label: "Porcentaje (%)" },
  { key: "FIXED_AMOUNT", label: "Monto fijo (S/)" },
  { key: "FIXED_PRICE", label: "Precio fijo (S/)" },
  { key: "PRICE_LIST", label: "Lista de precios" },
];

export default {
  components: { FormError, FormSelect },
  data() {
    return {
      couponTypes: COUPON_TYPES,
      vehicleOptions: ["Sedan", "SUV", "VAN", "Sprinter"],
      cities: [],
      districts: [],
      priceTypes: [],
      showAdvanced: false,
      loadingCoupon: false, // evita limpiar district_ids al cargar en edición
      form: {
        code: null,
        description: null,
        coupon_type: "PERCENTAGE",
        discount_value: null,
        price_type_id: null,
        start_date: null,
        end_date: null,
        max_uses: null,
        max_uses_per_user: null,
        city_id: 0, // 0 = global
        district_ids: [],
        vehicle_types: [],
        route_type: "", // "" = ambas
        max_passengers: null,
        max_luggage: null,
      },
      editing: false,
    };
  },
  computed: {
    isPriceList() {
      return this.form.coupon_type === "PRICE_LIST";
    },
    valuePlaceholder() {
      return this.form.coupon_type === "PERCENTAGE" ? "Ej: 15 (%)" : "Ej: 49.90";
    },
    cityItems() {
      return [
        { key: 0, label: "Todas las ciudades (global)" },
        ...this.cities.map((c) => ({ key: c.id, label: c.name })),
      ];
    },
    priceTypeItems() {
      return this.priceTypes.map((p) => ({ key: p.id, label: p.name }));
    },
  },
  validations() {
    return {
      form: {
        code: { required },
        discount_value: {
          required: (v) => this.isPriceList || (v !== null && v !== "" && Number(v) > 0),
        },
        price_type_id: {
          required: (v) => !this.isPriceList || (v !== null && v !== "" && v !== 0),
        },
      },
    };
  },
  watch: {
    "$route.params.id": function () {
      if (this.$route.params.id) this.load(this.$route.params.id);
    },
    // Al cambiar la ciudad del cupón: recargar distritos de esa ciudad y limpiar
    // la selección (los distritos de una ciudad no valen para otra). Se omite
    // durante la carga en edición para no perder los distritos guardados.
    "form.city_id": function (newVal) {
      if (this.loadingCoupon) return;
      this.form.district_ids = [];
      if (newVal) {
        this.fetchDistricts(newVal);
      } else {
        this.districts = [];
      }
    },
  },
  methods: {
    async loadCities() {
      try {
        const res = await GeographyService.listCities();
        this.cities = (res.payload || []).filter((c) => c.is_active);
      } catch (e) {
        console.error(e);
      }
    },
    async fetchDistricts(cityId) {
      try {
        const res = await GeographyService.listDistricts(cityId);
        this.districts = res.payload || [];
      } catch (e) {
        console.error(e);
        this.districts = [];
      }
    },
    async loadPriceTypes() {
      try {
        const res = await PriceTypeService.list();
        this.priceTypes = res.payload || [];
      } catch (e) {
        console.error(e);
      }
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector(".form-error"),
            updateHistory: false,
            offset: -50,
          });
        });
        return;
      }
      try {
        this.$store.dispatch("app/loading", true);
        const f = this.form;
        const data = {
          code: f.code,
          description: f.description || null,
          coupon_type: f.coupon_type,
          discount_value: this.isPriceList ? 0 : parseFloat(f.discount_value),
          price_type_id: this.isPriceList ? parseInt(f.price_type_id) : null,
          start_date: f.start_date || null,
          end_date: f.end_date || null,
          max_uses: f.max_uses ? parseInt(f.max_uses) : null,
          max_uses_per_user: f.max_uses_per_user ? parseInt(f.max_uses_per_user) : null,
          city_id: f.city_id ? parseInt(f.city_id) : null,
          district_ids: f.city_id ? f.district_ids : [],
          vehicle_types: f.vehicle_types,
          route_type: f.route_type || null,
          max_passengers: f.max_passengers ? parseInt(f.max_passengers) : null,
          max_luggage: f.max_luggage !== null && f.max_luggage !== "" ? parseInt(f.max_luggage) : null,
          // discount_type lo deriva el backend desde coupon_type (backward compat).
          discount_type: f.coupon_type === "PERCENTAGE" ? "percent" : "fixed",
        };

        if (!this.editing) {
          await CouponService.save(data);
          Alert.success("Cupón creado correctamente");
          this.$router.push({ name: "config-coupon-list" });
        } else {
          const updateData = { ...data };
          delete updateData.code;
          await CouponService.update(this.$route.params.id, updateData);
          Alert.success("Cupón actualizado correctamente");
          this.load(this.$route.params.id);
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors?.message || "Error al guardar el cupón");
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const response = await CouponService.get(id);
        const p = response.payload;
        // Guard: al asignar city_id el watcher limpiaría district_ids; lo evitamos
        // durante la carga para conservar los distritos guardados del cupón.
        this.loadingCoupon = true;
        this.form = {
          code: p.code,
          description: p.description || null,
          coupon_type: p.coupon_type || "PERCENTAGE",
          discount_value: p.discount_value,
          price_type_id: p.price_type_id != null ? p.price_type_id : null,
          start_date: p.start_date ? p.start_date.substring(0, 10) : null,
          end_date: p.end_date ? p.end_date.substring(0, 10) : null,
          max_uses: p.max_uses || null,
          max_uses_per_user: p.max_uses_per_user || null,
          city_id: p.city_id != null ? p.city_id : 0,
          district_ids: (p.districts || []).map((d) => d.id),
          vehicle_types: p.vehicle_types || [],
          route_type: p.route_type || "",
          max_passengers: p.max_passengers != null ? p.max_passengers : null,
          max_luggage: p.max_luggage != null ? p.max_luggage : null,
        };
        // Cargar los distritos de la ciudad del cupón (no la del store).
        if (this.form.city_id) {
          await this.fetchDistricts(this.form.city_id);
        }
        // Liberar el guard tras el flush del watcher.
        this.$nextTick(() => {
          this.loadingCoupon = false;
        });
        // Abrir restricciones si el cupón tiene alguna.
        if (
          this.form.city_id ||
          this.form.district_ids.length ||
          this.form.vehicle_types.length ||
          this.form.route_type ||
          this.form.max_passengers != null ||
          this.form.max_luggage != null
        ) {
          this.showAdvanced = true;
        }
        this.editing = true;
      } catch (e) {
        console.error(e);
        Alert.error(e.errors?.message || "Error al cargar el cupón");
        this.$router.push({ name: "config-coupon-list" });
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
  },
  async created() {
    await Promise.all([this.loadCities(), this.loadPriceTypes()]);
    if (this.$route.params.id) this.load(this.$route.params.id);
  },
};
</script>

<style lang="stylus" scoped>
.adv-toggle
  background none
  border none
  padding 0
  font-size 15px
  font-weight 600
  color #1a1f2e
  cursor pointer
  display inline-flex
  align-items center
  gap 4px

.check-inline
  margin-right 18px
  margin-bottom 6px
  font-weight 400
  cursor pointer
  input
    margin-right 5px

.district-multi
  height auto
  min-height 120px
  padding 6px
</style>
